import React, { useState, useEffect } from "react";
import { useWebhooks, useUpdateWebhooks, useTestWebhook, useScannerState, useStartScanner, useCaptchaConfig, useUpdateCaptchaConfig, useCaptchaBalance, useScrapingProvider, useUpdateScrapingProvider, useTestScrapingProvider } from "@/lib/queries";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Save, Send, Check, X, Shield, Wallet, Eye, EyeOff, Bell, Info, Download, RefreshCw, Globe, Zap, AlertCircle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/cr";
import BrightDataCollectorsPanel from "@/components/BrightDataCollectorsPanel";

const LABEL = "text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5";
const INPUT = "w-full bg-[#050505] border border-[#262626] text-white text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252] font-mono";

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { data: cfg } = useWebhooks();
  const update = useUpdateWebhooks();
  const test = useTestWebhook();
  const { data: scanner } = useScannerState();
  const startScanner = useStartScanner();

  const [form, setForm] = useState({
    discord_url: "",
    discord_enabled: false,
    telegram_bot_token: "",
    telegram_chat_id: "",
    telegram_enabled: false,
  });

  const [interval, setInterval_] = useState(20);

  useEffect(() => {
    if (cfg) {
      setForm({
        discord_url: cfg.discord_url || "",
        discord_enabled: !!cfg.discord_enabled,
        telegram_bot_token: cfg.telegram_bot_token || "",
        telegram_chat_id: cfg.telegram_chat_id || "",
        telegram_enabled: !!cfg.telegram_enabled,
      });
    }
  }, [cfg]);

  useEffect(() => {
    if (scanner?.interval_seconds) setInterval_(scanner.interval_seconds);
  }, [scanner]);

  const save = () => {
    update.mutate(form, {
      onSuccess: () => toast.success("Webhook settings saved"),
      onError: () => toast.error("Failed to save"),
    });
  };

  const testTarget = async (target) => {
    // Persist current form values first so the backend tests what the user actually typed,
    // not the previously-saved URL. This fixes the "I pasted a new webhook and still got 404" trap.
    try {
      await update.mutateAsync(form);
    } catch (e) {
      toast.error("Failed to save before test", { description: e?.response?.data?.detail });
      return;
    }
    test.mutate(target, {
      onSuccess: (data) => {
        if (data.ok) toast.success(`${target} test sent`);
        else toast.error(`${target} test failed`, { description: data.error || `status ${data.status}` });
      },
      onError: (e) => toast.error(`${target} test failed`, { description: e?.response?.data?.detail || "Check config" }),
    });
  };

  return (
    <div className="p-6 max-w-4xl w-full mx-auto" data-testid="settings-page">
      <header className="mb-6">
        <h1 className="font-heading text-4xl tracking-tighter font-black uppercase">
          Settings
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-1">
          {isAdmin
            ? "Notifications, scanner cadence and captcha automation."
            : "Where do you want to be pinged when a watchlist match shows up?"}
        </p>
      </header>

      {/* How alerts work — visible to everyone */}
      <section
        className="bg-[#EAB308]/5 border border-[#EAB308]/30 p-4 rounded-sm mb-6 flex gap-3"
        data-testid="alerts-howto"
      >
        <Bell className="w-5 h-5 text-[#EAB308] shrink-0 mt-0.5" />
        <div className="text-[12px] leading-relaxed text-[#D4D4D4]">
          <strong className="text-white">How alerts work</strong> — Create a rule in <a href="/app/watchlist" className="text-[#EAB308] hover:underline">/app/watchlist</a> (e.g. <em>Pokemon Surging Sparks ETB, max $50, US only</em>). Every scan, the bot checks new listings against your rules. Matches appear in the <strong className="text-[#EAB308]">Live Alerts</strong> column on the right of <a href="/app/radar" className="text-[#EAB308] hover:underline">/app/radar</a> and (optionally) get forwarded to your Discord / Telegram below.
        </div>
      </section>

      {/* Scanner — admin only */}
      {isAdmin && (
      <section className="bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm mb-6" data-testid="scanner-settings">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-base font-bold uppercase tracking-wider">Scanner</h2>
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#A855F7]">Admin · global</span>
        </div>
        <p className="text-[11px] text-[#737373] mb-3">
          How often the bot polls retailers for new listings. Lower interval = faster alerts but more bandwidth. Shared by every user on this CardRadar instance.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <div className={LABEL}>Interval (seconds)</div>
            <input
              data-testid="scanner-interval-input"
              type="number"
              min="5"
              max="600"
              value={interval}
              onChange={(e) => setInterval_(Number(e.target.value))}
              className={INPUT}
            />
          </div>
          <div className="text-xs font-mono text-[#A3A3A3]">
            <div>Status: <span className={scanner?.active ? "text-[#22C55E]" : "text-[#A3A3A3]"}>{scanner?.active ? "RUNNING" : "STOPPED"}</span></div>
            <div>Total scans: <span className="text-white">{scanner?.total_scans ?? 0}</span></div>
          </div>
          <button
            data-testid="apply-interval-btn"
            onClick={() => startScanner.mutate(interval, { onSuccess: () => toast.success("Interval applied") })}
            className="bg-[#EAB308] text-black font-bold uppercase tracking-wider text-xs py-2.5 rounded-sm hover:bg-[#FDE047] transition-all flex items-center justify-center gap-2"
          >
            <Save className="w-4 h-4" /> Apply & start
          </button>
        </div>
      </section>
      )}

      {/* Discord */}
      <section className="bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm mb-6" data-testid="discord-settings">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-base font-bold uppercase tracking-wider flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#5865F2]" />
            Discord webhook
          </h2>
          <label className="flex items-center gap-2 text-xs text-[#A3A3A3] cursor-pointer">
            <input
              type="checkbox"
              data-testid="discord-enabled"
              checked={form.discord_enabled}
              onChange={(e) => setForm({ ...form, discord_enabled: e.target.checked })}
              className="w-3.5 h-3.5 accent-[#EAB308]"
            />
            Enabled
          </label>
        </div>
        <div className={LABEL}>Webhook URL</div>
        <input
          data-testid="discord-url-input"
          type="text"
          value={form.discord_url}
          onChange={(e) => setForm({ ...form, discord_url: e.target.value })}
          placeholder="https://discord.com/api/webhooks/..."
          className={INPUT}
        />
        <p className="text-[11px] text-[#737373] mt-2 leading-relaxed">
          Get a real-time DM in Discord every time one of your watches matches a new listing. Server Settings → Integrations → Webhooks → New Webhook → Copy URL.
        </p>
        <div className="flex gap-2 mt-3">
          <button
            data-testid="discord-test-btn"
            onClick={() => testTarget("discord")}
            disabled={!form.discord_url}
            className="px-3 py-2 text-xs uppercase font-bold tracking-wider border border-[#262626] hover:border-[#A3A3A3] hover:bg-[#1A1A1A] flex items-center gap-1.5 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-3.5 h-3.5" /> Send test ping
          </button>
        </div>
      </section>

      {/* Telegram */}
      <section className="bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm mb-6" data-testid="telegram-settings">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-base font-bold uppercase tracking-wider flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#229ED9]" />
            Telegram bot
          </h2>
          <label className="flex items-center gap-2 text-xs text-[#A3A3A3] cursor-pointer">
            <input
              type="checkbox"
              data-testid="telegram-enabled"
              checked={form.telegram_enabled}
              onChange={(e) => setForm({ ...form, telegram_enabled: e.target.checked })}
              className="w-3.5 h-3.5 accent-[#EAB308]"
            />
            Enabled
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className={LABEL}>Bot token</div>
            <input
              data-testid="telegram-token-input"
              type="text"
              value={form.telegram_bot_token}
              onChange={(e) => setForm({ ...form, telegram_bot_token: e.target.value })}
              placeholder="1234567890:ABC-..."
              className={INPUT}
            />
          </div>
          <div>
            <div className={LABEL}>Chat ID</div>
            <input
              data-testid="telegram-chat-input"
              type="text"
              value={form.telegram_chat_id}
              onChange={(e) => setForm({ ...form, telegram_chat_id: e.target.value })}
              placeholder="-1001234567890"
              className={INPUT}
            />
          </div>
        </div>
        <p className="text-[11px] text-[#737373] mt-2 leading-relaxed">
          Same as Discord, but the alert pings you on Telegram instead — great for phone notifications. Create a bot via <code className="text-[#A3A3A3]">@BotFather</code>, then get the chat id by messaging your bot and visiting <code className="text-[#A3A3A3]">https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code>.
        </p>
        <div className="flex gap-2 mt-3">
          <button
            data-testid="telegram-test-btn"
            onClick={() => testTarget("telegram")}
            disabled={!form.telegram_bot_token || !form.telegram_chat_id}
            className="px-3 py-2 text-xs uppercase font-bold tracking-wider border border-[#262626] hover:border-[#A3A3A3] hover:bg-[#1A1A1A] flex items-center gap-1.5 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-3.5 h-3.5" /> Send test ping
          </button>
        </div>
      </section>

      <div className="flex justify-end">
        <button
          data-testid="save-settings-btn"
          onClick={save}
          disabled={update.isPending}
          className="bg-[#EAB308] text-black font-bold uppercase tracking-wider text-xs px-6 py-2.5 rounded-sm hover:bg-[#FDE047] transition-all flex items-center gap-2 disabled:opacity-60"
        >
          <Save className="w-4 h-4" />
          Save settings
        </button>
      </div>

      <ScrapingProviderSettings />
      <BrightDataCollectorsPanel isAdmin={isAdmin} />
      <CaptchaSettings isAdmin={isAdmin} />
      <UpdateChecker />
    </div>
  );
}

// ─── Scraping Provider (Bright Data Scraping Browser) ─────────────────────
//
// Why this exists: BIG W / Target AU / Kmart AU all sit behind Akamai
// Bot Manager. Local Playwright loses ~90% of attempts to Akamai's bot
// score. Bright Data Scraping Browser is a managed remote-Chrome service
// with residential IPs + real JA3 fingerprints + built-in CAPTCHA solving
// that ~90%+ of Akamai-protected sites accept. Users pay Bright Data
// directly (BYO credentials), CardRadar doesn't carry the bandwidth bill.
const ALL_RETAILERS = [
  // Default-on (Akamai/DataDome protected → genuinely need Bright Data)
  { id: "bigw",          name: "BIG W",          protected: true },
  { id: "target_au",     name: "Target AU",      protected: true },
  { id: "kmart_au",      name: "Kmart AU",       protected: true },
  { id: "target",        name: "Target (US)",    protected: true },
  // Less aggressive WAFs — usually work fine with local Chromium
  { id: "jbhifi",        name: "JB Hi-Fi",       protected: false },
  { id: "pokemoncenter", name: "Pokemon Center", protected: false },
  { id: "tcgplayer",     name: "TCGplayer",      protected: false },
  { id: "ebgames_au",    name: "EB Games AU",    protected: false },
];

function ScrapingProviderSettings() {
  const { data: cfg, isLoading } = useScrapingProvider();
  const update = useUpdateScrapingProvider();
  const test = useTestScrapingProvider();
  const [form, setForm] = useState({
    enabled: false,
    provider: "local",
    bd_username: "",
    bd_password: "",  // empty string = keep existing if .._set is true, until user types
    retailers: [],
  });
  const [pwTouched, setPwTouched] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (cfg) {
      setForm({
        enabled: !!cfg.enabled,
        provider: cfg.provider || "local",
        bd_username: cfg.bd_username || "",
        bd_password: "",  // never pre-fill the password input — UI shows "•••••• (set)" instead
        retailers: cfg.retailers || [],
      });
      setPwTouched(false);
    }
  }, [cfg]);

  const passwordIsSet = !!cfg?.bd_password_set;
  const usingBrightData = form.enabled && form.provider === "brightdata";

  const save = (next) => {
    const payload = { ...form, ...next };
    // bd_password convention: null = "keep existing", anything else = "set to this".
    // We send null when the user hasn't touched the field and a password is already set.
    const body = {
      enabled: payload.enabled,
      provider: payload.provider,
      bd_username: payload.bd_username,
      bd_password: pwTouched ? payload.bd_password : (passwordIsSet ? null : payload.bd_password),
      retailers: payload.retailers,
    };
    update.mutate(body, {
      onSuccess: () => toast.success("Scraping provider settings saved"),
      onError: (e) => toast.error("Save failed", { description: e?.response?.data?.detail }),
    });
    setForm(payload);
  };

  const runTest = async () => {
    setTestResult({ pending: true });
    try {
      const data = await test.mutateAsync();
      setTestResult(data);
      if (data.ok) toast.success("Bright Data connection works");
      else toast.error("Test failed", { description: data.error });
    } catch (e) {
      setTestResult({ ok: false, error: e?.message || "Network error" });
    }
  };

  const toggleRetailer = (id) => {
    const next = form.retailers.includes(id)
      ? form.retailers.filter((r) => r !== id)
      : [...form.retailers, id];
    save({ retailers: next });
  };

  if (isLoading) return null;

  return (
    <section className="bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm mt-6" data-testid="scraping-provider-settings">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-heading text-xl font-black uppercase tracking-tight flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#EAB308]" />
            Scraping Provider
          </h2>
          <p className="text-xs text-[#A3A3A3] mt-1 max-w-xl">
            Local Chromium fails ~90% of attempts on Akamai-protected retailers (BIG W, Target AU, Kmart AU).
            Route those through <span className="text-[#EAB308]">Bright Data Scraping Browser</span> — a remote
            Chrome with residential IPs and CAPTCHA solving — for ~90%+ pass rate. You pay Bright Data
            directly (~$8/GB ≈ $0.02–0.04 per scalp attempt).
          </p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer shrink-0" data-testid="scraping-enabled-toggle">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => save({ enabled: e.target.checked, provider: e.target.checked ? "brightdata" : "local" })}
            className="w-4 h-4 accent-[#EAB308]"
          />
          <span className="text-xs uppercase tracking-wider font-bold text-white">
            {form.enabled ? "On" : "Off"}
          </span>
        </label>
      </div>

      {usingBrightData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <div className={LABEL}>Bright Data Username</div>
              <input
                type="text"
                className={INPUT}
                placeholder="brd-customer-hl_xxxxxxxx-zone-yourzone"
                value={form.bd_username}
                onChange={(e) => setForm({ ...form, bd_username: e.target.value })}
                onBlur={() => save({})}
                data-testid="bd-username-input"
              />
              <div className="text-[10px] text-[#525252] mt-1">
                From Bright Data → your Scraping Browser zone → Access parameters → Username.
              </div>
            </div>
            <div>
              <div className={LABEL}>Bright Data Password</div>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  className={INPUT + " pr-10"}
                  placeholder={passwordIsSet && !pwTouched ? "•••••••• (set)" : "Paste zone password"}
                  value={form.bd_password}
                  onChange={(e) => { setForm({ ...form, bd_password: e.target.value }); setPwTouched(true); }}
                  onBlur={() => { if (pwTouched) save({}); }}
                  data-testid="bd-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#737373] hover:text-white"
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="text-[10px] text-[#525252] mt-1">
                Stored locally on this machine only. Never sent to CardRadar cloud.
              </div>
            </div>
          </div>

          <div className="mb-4">
            <div className={LABEL}>Use Bright Data for these retailers</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {ALL_RETAILERS.map((r) => {
                const on = form.retailers.includes(r.id);
                return (
                  <button
                    type="button"
                    key={r.id}
                    onClick={() => toggleRetailer(r.id)}
                    className={`text-left px-3 py-2 rounded-sm border text-xs transition-all ${on
                      ? "border-[#EAB308] bg-[#EAB308]/10 text-white"
                      : "border-[#262626] hover:border-[#525252] text-[#A3A3A3]"}`}
                    data-testid={`bd-retailer-${r.id}`}
                  >
                    <div className="font-bold flex items-center gap-1.5">
                      {on && <Check className="w-3 h-3 text-[#EAB308]" />}
                      {r.name}
                    </div>
                    {r.protected && (
                      <div className="text-[9px] uppercase tracking-wider text-[#EAB308] font-mono mt-0.5">
                        Akamai-protected
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] text-[#525252] mt-2">
              Tip: leave non-protected retailers (JB Hi-Fi, Pokemon Center, TCGplayer, EB Games) on local
              Chromium to save Bright Data credits — they don&apos;t need WAF bypass.
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={runTest}
              disabled={test.isPending || !form.bd_username || (!passwordIsSet && !form.bd_password)}
              className="px-4 py-2 text-xs uppercase font-bold tracking-wider border border-[#EAB308] text-[#EAB308] hover:bg-[#EAB308]/10 transition-all flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="bd-test-button"
            >
              {test.isPending || testResult?.pending ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
              Test Connection
            </button>
            {testResult && !testResult.pending && (
              <div className={`text-xs flex-1 min-w-0 ${testResult.ok ? "text-[#22C55E]" : "text-[#EF4444]"}`} data-testid="bd-test-result">
                {testResult.ok ? (
                  <div className="flex items-start gap-1.5">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                    <div className="font-mono break-all">
                      <div className="uppercase font-bold tracking-wider mb-1">Connected — Bright Data response:</div>
                      <div className="text-[#A3A3A3] whitespace-pre-wrap">{testResult.preview}</div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-1.5">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>{testResult.error}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function GitHubTokenInput({ onSaved }) {
  const [token, setToken] = useState("");
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/desktop/github-token")
      .then(({ data }) => { setStatus(data); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/desktop/github-token", { token });
      setStatus(data);
      toast.success(data.message || "Token saved");
      setToken("");
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save token");
    }
    setSaving(false);
  };
  const clear = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/desktop/github-token", { token: "" });
      setStatus(data);
      toast.success("Token cleared");
      if (onSaved) onSaved();
    } catch {
      toast.error("Failed to clear token");
    }
    setSaving(false);
  };
  if (!loaded) return null;
  return (
    <div className="space-y-2">
      {status?.configured ? (
        <div className="flex items-center justify-between gap-2 bg-[#0a0a0a] border border-[#1f1f1f] rounded-sm px-3 py-2">
          <span className="text-[11px] font-mono text-[#22C55E]">✓ Token configured: {status.preview}</span>
          <button
            data-testid="clear-github-token-btn"
            onClick={clear}
            disabled={saving}
            className="text-[10px] uppercase tracking-wider text-[#EF4444] hover:underline disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      ) : null}
      <input
        data-testid="github-token-input"
        type="password"
        placeholder="ghp_… or github_pat_…"
        value={token}
        onChange={(e) => setToken(e.target.value)}
        className="w-full bg-[#050505] border border-[#262626] rounded-sm px-3 py-2 text-xs font-mono text-white placeholder-[#525252] focus:border-[#EAB308] focus:outline-none"
      />
      <div className="flex items-center justify-between gap-2">
        <a
          href="https://github.com/settings/tokens?type=beta"
          target="_blank" rel="noopener noreferrer"
          className="text-[10px] text-[#A3A3A3] hover:text-[#EAB308] underline"
        >
          Create a fine-grained token →
        </a>
        <button
          data-testid="save-github-token-btn"
          onClick={save}
          disabled={!token.trim() || saving}
          className="bg-[#EAB308] text-black font-bold text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-sm hover:bg-[#FDE047] disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save token"}
        </button>
      </div>
      <div className="text-[10px] text-[#737373] leading-relaxed">
        <b>Permissions needed</b> (fine-grained):  Repository access: <i>Only select repositories → juansolo9000/cardradar</i>. Permissions: <i>Contents: Read-only</i>. Token is stored only in <span className="font-mono text-[#A3A3A3]">~/.cardradar/github_token.txt</span> on this machine — never uploaded.
      </div>
    </div>
  );
}


function UpdateChecker() {
  const [state, setState] = useState({ loading: false, result: null });
  const [installing, setInstalling] = useState(false);
  const [installMsg, setInstallMsg] = useState(null);
  const checkNow = async () => {
    setState({ loading: true, result: null });
    setInstallMsg(null);
    try {
      const { data } = await api.get("/desktop/check-updates");
      setState({ loading: false, result: data });
    } catch (e) {
      setState({ loading: false, result: { error: e?.message || "Network error" } });
    }
  };
  const installNow = async () => {
    setInstalling(true);
    setInstallMsg("Starting download…");
    try {
      const { data } = await api.post("/desktop/install-update");
      if (data?.ok) {
        setInstallMsg(data.message + " (this window will go blank when the new .exe launches)");
      } else {
        setInstallMsg("Update failed: " + (data?.message || "unknown"));
        setInstalling(false);
      }
    } catch (e) {
      setInstallMsg("Install failed: " + (e?.response?.data?.detail || e?.message || "network error"));
      setInstalling(false);
    }
  };
  const r = state.result;
  return (
    <section
      data-testid="update-checker"
      className="mt-6 bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="font-heading text-xl font-black uppercase tracking-tight text-white">App version</div>
          <div className="text-[12px] text-[#A3A3A3]">Check for a newer CardRadar build.</div>
        </div>
        <button
          data-testid="check-updates-btn"
          onClick={checkNow}
          disabled={state.loading}
          className="bg-[#EAB308] text-black font-bold text-[11px] uppercase tracking-wider px-4 py-2 rounded-sm hover:bg-[#FDE047] disabled:opacity-50 flex items-center gap-2"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${state.loading ? "animate-spin" : ""}`} />
          {state.loading ? "Checking…" : "Check for updates"}
        </button>
      </div>
      {r && r.error && (
        <div className="text-xs text-[#EF4444] font-mono">Error: {r.error}</div>
      )}
      {r && !r.update_available && !r.error && (
        <div className="bg-[#0a0a0a] border border-[#1f1f1f] rounded-sm px-3 py-2 text-xs">
          <div className="flex items-center gap-2 text-[#22C55E] mb-1">
            <Check className="w-3.5 h-3.5" />
            <span className="font-bold">{r.message || "Up to date"}</span>
          </div>
          {r.current_sha && (
            <div className="font-mono text-[10px] text-[#737373]">Installed build: {r.current_sha.slice(0, 12)}</div>
          )}
          {r.reason === "cloud_mode" && (
            <div className="text-[10px] text-[#A3A3A3] mt-1">Updates only apply when running the desktop .exe — not the web preview.</div>
          )}
          {r.reason === "no_updater_module" && (
            <div className="text-[10px] text-[#FCA5A5] mt-1">⚠ This build doesn&apos;t have the auto-updater. <a href="https://github.com/juansolo9000/cardradar/releases/latest" target="_blank" rel="noopener noreferrer" className="text-[#EAB308] hover:underline">Download the latest .exe manually</a> to enable auto-updates from then on.</div>
          )}
          {r.reason === "no_releases_yet" && (
            <div className="text-[10px] text-[#FCA5A5] mt-1">
              ⚠ The cloud release proxy has no releases to show yet. Either the GH Actions build hasn&apos;t published anything, or the cloud backend is missing the <span className="font-mono">GITHUB_RELEASE_PAT</span> env var. Contact support.
            </div>
          )}
          {r.reason === "cloud_pat_missing" && (
            <div className="text-[10px] text-[#FCA5A5] mt-1">
              ⚠ Cloud release proxy returned a server error — <span className="font-mono">GITHUB_RELEASE_PAT</span> is missing or revoked. Contact support.
            </div>
          )}
          {r.reason === "release_body_missing_sha" && (
            <div className="text-[10px] text-[#FCA5A5] mt-1">⚠ The latest Release exists but its body has no &apos;commit &lt;sha&gt;&apos; line. The auto-updater can&apos;t compare versions.</div>
          )}
          {r.reason === "cloud_unreachable" && (
            <div className="text-[10px] text-[#FCA5A5] mt-1">⚠ Couldn&apos;t reach the cloud release proxy. Check your internet connection.</div>
          )}
        </div>
      )}
      {r && r.update_available && (
        <div className="bg-[#1E3A8A]/15 border border-[#3B82F6]/40 rounded-sm px-3 py-3 text-xs">
          <div className="flex items-center justify-between mb-2 gap-2">
            <span className="font-bold text-white">{r.release_name || "Update available"}</span>
            {r.is_mandatory && (
              <span className="text-[9px] uppercase tracking-wider font-bold text-[#FCA5A5] border border-[#DC2626]/50 px-1.5 py-0.5 rounded-sm">MANDATORY</span>
            )}
          </div>
          <div className="font-mono text-[10px] text-[#737373] mb-2">
            {r.current_sha?.slice(0, 12)} → <span className="text-[#22C55E]">{r.latest_sha?.slice(0, 12)}</span>
          </div>
          <pre className="whitespace-pre-wrap text-[11px] text-[#D4D4D4] bg-[#050505] border border-[#1f1f1f] p-2 rounded-sm mb-2 max-h-32 overflow-auto">{r.body}</pre>
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="install-update-btn"
              onClick={installNow}
              disabled={installing}
              className="inline-flex items-center gap-2 bg-[#EAB308] text-black font-bold text-[11px] uppercase tracking-wider px-3 py-2 rounded-sm hover:bg-[#FDE047] disabled:opacity-50"
            >
              <Download className="w-3.5 h-3.5" />
              {installing ? "Installing…" : "Install now"}
            </button>
            <a
              data-testid="download-update-link"
              href={r.download_url}
              target="_blank" rel="noopener noreferrer"
              className="text-[10px] text-[#A3A3A3] underline hover:text-white"
            >
              or download manually
            </a>
          </div>
          {installMsg && (
            <div className="mt-2 text-[10px] text-[#FCD34D] font-mono">{installMsg}</div>
          )}
        </div>
      )}
    </section>
  );
}

function CaptchaSettings({ isAdmin }) {
  const { data: cfg } = useCaptchaConfig();
  const { data: sp } = useScrapingProvider();
  const update = useUpdateCaptchaConfig();
  const balance = useCaptchaBalance();
  const [key, setKey] = useState("");
  const [show, setShow] = useState(false);

  if (!isAdmin) return null;
  // When Bright Data is on for ALL Akamai-protected retailers, 2captcha is
  // effectively redundant (Bright Data has built-in CAPTCHA solving). Surface
  // that to the admin so they don't think they need both.
  const bdActive = !!(sp?.enabled && sp?.provider === "brightdata");
  const protectedSet = new Set(["bigw", "target_au", "kmart_au", "target"]);
  const allProtectedViaBD = bdActive && [...protectedSet].every((id) => (sp?.retailers || []).includes(id));

  const save = () => {
    update.mutate(
      { api_key: key },
      {
        onSuccess: () => {
          toast.success("2captcha API key saved (encrypted)");
          setKey("");
        },
        onError: (e) => toast.error("Save failed", { description: e?.response?.data?.detail }),
      },
    );
  };

  const checkBalance = () => {
    balance.mutate(undefined, {
      onSuccess: (r) => toast.success(`Balance: $${r.balance.toFixed(2)} USD`),
      onError: (e) => toast.error("Balance check failed", { description: e?.response?.data?.detail }),
    });
  };

  return (
    <section className="bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm mt-6" data-testid="captcha-settings">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-base font-bold uppercase tracking-wider flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#A855F7]" />
          2captcha integration
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#A855F7]">Admin · global</span>
          {cfg?.configured && (
            <div className="text-[11px] font-mono text-[#A3A3A3]">
              <span className="text-[#22C55E]">●</span> Configured · {cfg.key_masked}
            </div>
          )}
        </div>
      </div>
      <p className="text-[11px] text-[#737373] mb-3 leading-relaxed">
        Retailers (Pokemon Center, TCGplayer, Target…) sometimes show a reCAPTCHA puzzle during checkout. Scalp Mode can&apos;t solve those itself. <a href="https://2captcha.com" target="_blank" rel="noopener noreferrer" className="text-[#EAB308] hover:underline">2captcha.com</a> is a 3rd-party service where real humans solve those puzzles for ~$1–3 per 1,000 solves. Paste your API key here → Scalp Mode forwards any reCAPTCHA it hits to 2captcha automatically, gets a solve back in ~10 seconds, and continues the purchase. <strong className="text-[#FBBF24]">Without a key</strong>, Scalp Mode pauses when challenged and asks you to solve the puzzle manually in the dashboard.
      </p>
      {allProtectedViaBD && (
        <div className="text-[11px] text-[#22C55E] bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-sm p-2 mb-3 flex items-start gap-2" data-testid="captcha-bd-redundant-hint">
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <div>
            Bright Data is routing all Akamai-protected retailers and has built-in CAPTCHA solving.
            2captcha is <strong>optional</strong> — only needed if a CAPTCHA pops up on a retailer NOT
            routed through Bright Data (Pokemon Center, JB Hi-Fi, TCGplayer, EB Games).
          </div>
        </div>
      )}
      <div className={LABEL}>API key</div>
      <div className="relative">
        <input
          data-testid="captcha-key-input"
          type={show ? "text" : "password"}
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder={cfg?.configured ? "Replace existing key…" : "32-character 2captcha key"}
          className={INPUT + " pr-10"}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[#737373] hover:text-white"
          data-testid="captcha-key-show"
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          data-testid="captcha-save-btn"
          onClick={save}
          disabled={!key || update.isPending}
          className="px-4 py-2 text-xs uppercase font-bold tracking-wider bg-[#A855F7] text-white hover:bg-[#C084FC] flex items-center gap-1.5 rounded-sm disabled:opacity-60"
        >
          <Save className="w-3.5 h-3.5" /> Save key
        </button>
        <button
          data-testid="captcha-balance-btn"
          onClick={checkBalance}
          disabled={!cfg?.configured || balance.isPending}
          className="px-3 py-2 text-xs uppercase font-bold tracking-wider border border-[#262626] hover:border-[#A3A3A3] hover:bg-[#1A1A1A] flex items-center gap-1.5 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Wallet className="w-3.5 h-3.5" /> Check balance
          {cfg?.balance != null && <span className="text-[#22C55E] font-mono">${cfg.balance.toFixed(2)}</span>}
        </button>
      </div>
    </section>
  );
}
